import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Card,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Popconfirm,
  Space,
  Tag,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import {
  fetchUserLevels,
  createUserLevel,
  updateUserLevel,
  deleteUserLevel,
  clearError,
} from '../../features/user/userSlice';
import { formatDateTime } from '../../utils/index';

const { Option } = Select;
const { TextArea } = Input;

const UserLevels = () => {
  const dispatch = useDispatch();
  const { userLevels, isLoading, error } = useSelector((state) => state.user);
  
  const [form] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  useEffect(() => {
    dispatch(fetchUserLevels());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // 表格列配置
  const columns = [
    {
      title: '等级ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '等级名称',
      dataIndex: 'levelName',
      key: 'levelName',
      render: (levelName, record) => (
        <Space>
          <Tag color="blue">Lv.{record.level}</Tag>
          <span>{levelName}</span>
        </Space>
      ),
    },
    {
      title: '等级数值',
      dataIndex: 'level',
      key: 'level',
      width: 100,
      render: (level) => <Tag color="green">{level}</Tag>,
    },
    {
      title: '余额范围',
      key: 'balanceRange',
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            最低: ¥{record.minBalance || 0}
          </div>
          {record.maxBalance && (
            <div style={{ fontSize: '12px' }}>
              最高: ¥{record.maxBalance}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '等级权益',
      dataIndex: 'privileges',
      key: 'privileges',
      render: (privileges) => (
        <div>
          {privileges && Object.keys(privileges).length > 0 ? (
            Object.entries(privileges).map(([key, value], index) => (
              <Tag key={index} color="orange" style={{ marginBottom: '4px' }}>
                {key}: {value}
              </Tag>
            ))
          ) : (
            <span style={{ color: '#999' }}>无</span>
          )}
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 1 ? 'green' : 'red'}>
          {status === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => date ? formatDateTime(date) : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="编辑">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定要删除此等级吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button type="text" icon={<DeleteOutlined />} danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 新增等级
  const handleAdd = () => {
    setEditingLevel(null);
    form.resetFields();
    setModalVisible(true);
  };

  // 编辑等级
  const handleEdit = (level) => {
    setEditingLevel(level);
    // 将privileges对象转换为JSON字符串用于编辑
    const privilegesStr = level.privileges && Object.keys(level.privileges).length > 0
      ? JSON.stringify(level.privileges, null, 2)
      : '';
    
    form.setFieldsValue({
      levelName: level.levelName,
      level: level.level,
      minBalance: level.minBalance,
      maxBalance: level.maxBalance,
      privileges: privilegesStr,
      status: level.status,
    });
    setModalVisible(true);
  };

  // 删除等级
  const handleDelete = async (levelId) => {
    try {
      await dispatch(deleteUserLevel(levelId)).unwrap();
      message.success('删除成功');
    } catch (error) {
      message.error(error);
    }
  };

  // 提交表单
  const handleSubmit = async (values) => {
    try {
      // 处理权益字段
      let privileges = {};
      if (values.privileges) {
        if (typeof values.privileges === 'string') {
          try {
            privileges = JSON.parse(values.privileges);
          } catch (e) {
            message.error('权益配置格式错误，请输入有效的JSON格式');
            return;
          }
        } else {
          privileges = values.privileges;
        }
      }

      const data = {
        level: values.level,
        levelName: values.levelName,
        minBalance: values.minBalance,
        maxBalance: values.maxBalance || null,
        privileges,
        status: values.status,
      };

      if (editingLevel) {
        await dispatch(updateUserLevel({
          levelId: editingLevel.id,
          data,
        })).unwrap();
        message.success('更新成功');
      } else {
        await dispatch(createUserLevel(data)).unwrap();
        message.success('创建成功');
      }
      
      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error(error);
    }
  };

  // 表格选择
  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">用户等级管理</h1>
        <p className="page-description">管理用户等级体系和权益配置</p>
      </div>

      <Card>
        {/* 操作按钮 */}
        <div className="table-toolbar">
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新增等级
            </Button>
            <Button
              danger
              disabled={selectedRowKeys.length === 0}
              icon={<DeleteOutlined />}
            >
              批量删除
            </Button>
          </Space>
          <div>
            共 {userLevels.length} 个等级
          </div>
        </div>

        {/* 等级表格 */}
        <Table
          columns={columns}
          dataSource={userLevels}
          rowKey="id"
          loading={isLoading}
          rowSelection={rowSelection}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          }}
        />
      </Card>

      {/* 新增/编辑等级模态框 */}
      <Modal
        title={editingLevel ? '编辑等级' : '新增等级'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="等级名称"
            name="levelName"
            rules={[{ required: true, message: '请输入等级名称' }]}
          >
            <Input placeholder="请输入等级名称，如：普通会员、银卡会员" />
          </Form.Item>

          <Form.Item
            label="等级数值"
            name="level"
            rules={[{ required: true, message: '请输入等级数值' }]}
          >
            <InputNumber
              min={1}
              max={10}
              placeholder="请输入等级数值 (1-10)"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            label="最低余额"
            name="minBalance"
            rules={[{ required: true, message: '请输入最低余额' }]}
          >
            <InputNumber
              min={0}
              placeholder="请输入最低余额"
              style={{ width: '100%' }}
              precision={2}
              addonBefore="¥"
            />
          </Form.Item>

          <Form.Item
            label="最高余额"
            name="maxBalance"
          >
            <InputNumber
              min={0}
              placeholder="请输入最高余额（可选）"
              style={{ width: '100%' }}
              precision={2}
              addonBefore="¥"
            />
          </Form.Item>

          <Form.Item
            label="等级权益"
            name="privileges"
            tooltip="以JSON格式配置等级权益，如：返点率、提现限额等"
          >
            <TextArea
              rows={4}
              placeholder='请输入权益配置（JSON格式），例如：&#10;{&#10;  "commission_rate": "5%",&#10;  "withdraw_limit": "10000",&#10;  "lottery_times": "3"&#10;}'
            />
          </Form.Item>

          <Form.Item
            label="状态"
            name="status"
            rules={[{ required: true, message: '请选择状态' }]}
            initialValue={1}
          >
            <Select placeholder="请选择状态">
              <Option value={1}>启用</Option>
              <Option value={0}>禁用</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingLevel ? '更新' : '创建'}
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                form.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserLevels;
